import React from "react";
import defaultImage from "../../Assets/fallbackimg_giftcard.png";
import pointIcon from "../../Assets/point_icon.svg";
import { formatPrice, getImageUrl } from "../Utility/Utility";
import { getLineStatus, getSellingPrice, LINE_STATUS } from "./cartPricing";
import CartQuantityDropdown from "./CartQuantityDropdown";

/* One cart line: a product at one denomination, with its product details.
   Out of stock or unavailable lines show a status chip in place of the
   quantity picker so they cannot be bought.
   The picker offers 1 up to the line's limit; the chosen quantity is sent as
   the line's new quantity. Remove sends quantity 0 through the same API. */
const CartItem = ({ line, translation, maxQuantity, isUpdating, onQuantityChange, onRemove, earning }) => {
  const { quantity, name, productImage, details, purchaseLimit } = line;
  const status = getLineStatus(line);
  const isPurchasable = status === LINE_STATUS.AVAILABLE;
  const denomination = Number(line.denomination) || 0;
  const sellingPrice = details ? getSellingPrice(line) : 0;
  const savedAmount = sellingPrice > 0 ? denomination - sellingPrice : 0;
  const displayName = name || details?.name;

  const highestQuantity = Math.max(maxQuantity, quantity);
  const quantityOptions = Array.from({ length: highestQuantity }, (_, index) => index + 1);

  const isOutOfStock = status === LINE_STATUS.OUT_OF_STOCK;
  const statusLabel = isOutOfStock ? translation?.out_of_stock : translation?.cartUnavailable;

  return (
    <div className={`cart-item ${isPurchasable ? "" : "cart-item--disabled"}`}>
      <div className="cart-item__image">
        <img
          src={getImageUrl(productImage || details?.image) || defaultImage}
          alt={displayName || ""}
          onError={(e) => {
            e.target.src = defaultImage;
          }}
        />
      </div>

      <div className="cart-item__body">
        <div className="cart-item__top">
          <div className="cart-item__info">
            <div className="cart-item__name">{displayName}</div>
            {denomination > 0 && (
              <div className="cart-item__value">
                ₹{formatPrice(denomination)} {translation?.value?.toLowerCase()}
              </div>
            )}
            {earning && (
              <div className="cart-item__earn">
                {translation?.earn}{" "}
                <img src={pointIcon} alt="" aria-hidden="true" />{" "}
                <b className="cart-item__earn-points">{earning.points.toLocaleString("en-IN")}</b>
                {earning.worth > 0 && (
                  <> {translation?.worth}{formatPrice(earning.worth)}</>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            className="cart-item__remove"
            aria-label={translation?.cartRemoveItem}
            disabled={isUpdating}
            onClick={() => onRemove(line)}
          >
            <i className="fa-solid fa-trash-can"></i>
          </button>
        </div>

        <div className="cart-item__footer">
          <div className="cart-item__bottom">
            <div className="cart-item__price">
              {sellingPrice > 0 && (
                <span className="cart-item__selling">₹{formatPrice(sellingPrice)}</span>
              )}
              {savedAmount > 0 && (
                <span className="cart-item__saved">
                  {translation?.cartSaved} ₹{formatPrice(savedAmount)}
                </span>
              )}
            </div>

            {isPurchasable ? (
              <CartQuantityDropdown
                value={quantity}
                options={quantityOptions}
                label={translation?.qty}
                disabled={isUpdating}
                onChange={(nextQuantity) => onQuantityChange(line, nextQuantity)}
              />
            ) : (
              <span className="cart-item__status">
                <span className="cart-item__status-text">{statusLabel}</span>
              </span>
            )}
          </div>

          {/* Hidden by default; clients that want it turn it on in their cart.css. */}
          {!isPurchasable && (
            <div className="cart-item__alert" role="note">
              <span className="cart-item__alert-text">
                {isOutOfStock ? translation?.cartItemOutOfStockMessage : translation?.cartItemUnavailableMessage}
              </span>
              <button
                type="button"
                className="cart-item__alert-remove"
                disabled={isUpdating}
                onClick={() => onRemove(line)}
              >
                {translation?.cartRemove}
              </button>
            </div>
          )}

          {purchaseLimit?.value > 0 && (
            <div className="cart-item__note">
              {purchaseLimit.type} {translation?.cartPurchaseLimit} ₹{formatPrice(purchaseLimit.value)}.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartItem;
